import { isRecurringIssueQuestionData } from '@animal-nuisance/constants/questionData';
import { useAnimalNuisanceStore } from '@animal-nuisance/hooks/useAnimalNuisanceStore';
import { RoutesList } from '@animal-nuisance/routes/Routes';
import { BinaryOptionType } from '@shared/types';
import { isBinaryOptionDefault } from '@shared/utils';
import { MultipleOptionsTemplate, TextAreaTemplate } from '@templates';
import { ErrorMessages } from '@types';
import { booleanSchema, parseZodErrors, textAreaInputSchema } from '@utils';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ZodError } from 'zod';

export const RecurringIssuePage = () => {
  const questionData = isRecurringIssueQuestionData;
  const {
    data: { recurringIssue },
    update,
  } = useAnimalNuisanceStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const navigate = useNavigate();
  const handleSelect = (result: string, errorPath: 'isRecurringIssue') => {
    setErrorMessages([]);
    switch (errorPath) {
      case 'isRecurringIssue':
        update('recurringIssue.isRecurringIssue', result === 'Yes');
        if (result === 'No') {
          update('recurringIssue.whenDidItHappen', '');
        }
        break;
    }
  };

  const getDefaultOption = (
    option: BinaryOptionType,
    errorPath: 'isRecurringIssue',
  ) => {
    switch (errorPath) {
      case 'isRecurringIssue':
        return recurringIssue
          ? isBinaryOptionDefault(option, recurringIssue.isRecurringIssue)
          : false;
    }
  };

  const radioOptions = questionData.radioOptions.map((radioOption) => ({
    ...radioOption,
    options: radioOption.options.map((option) => ({
      ...option,
      isDefault: getDefaultOption(
        option.labelText as BinaryOptionType,
        radioOption.errorPath as 'isRecurringIssue',
      ),
    })),
    onChange: (result: string) =>
      handleSelect(result, radioOption.errorPath as 'isRecurringIssue'),
  }));

  const handleChangeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setErrorMessages([]);
    update('recurringIssue.whenDidItHappen', e.target.value);
  };

  const buttonNext = {
    onClick: () => {
      try {
        const isHappeningData = booleanSchema(
          questionData.radioOptions[0].errorPath,
          'Select an option to continue.',
        ).parse({
          [questionData.radioOptions[0].errorPath]:
            recurringIssue.isRecurringIssue,
        });
        if (isHappeningData.isRecurringIssue === false) {
          navigate(RoutesList.UPLOAD_PHOTO);
        } else if (isHappeningData.isRecurringIssue === true) {
          textAreaInputSchema({
            fieldName: questionData.textArea.errorPath,
            requiredMessage: 'Enter details about the issue',
            maxLength: questionData.textArea.maxLimit,
            maxLengthMessage: `The text must be less than ${questionData.textArea.maxLimit} characters`,
          }).parse({
            [questionData.textArea.errorPath]:
              recurringIssue.whenDidItHappen || '',
          });
          navigate(RoutesList.UPLOAD_PHOTO);
        }
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error) => {
            if (error.path === 'isRecurringIssue') {
              return {
                message: 'Select an option to continue',
                path: error.path,
              };
            }
            return {
              message: error.message,
              path: error.path,
            };
          }),
        );
      }
    },
  };
  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.DESCRIBE_THE_ISSUE);
    },
  };

  return (
    <MultipleOptionsTemplate
      title={questionData.title}
      radioOptions={radioOptions}
      errorPath={questionData.key}
      errorMessages={errorMessages}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      children={
        recurringIssue &&
        recurringIssue.isRecurringIssue === true && (
          <div>
            <TextAreaTemplate
              id={questionData.textArea.id}
              labelText={questionData.textArea.title}
              description={questionData.textArea.description}
              errorPath={questionData.textArea.errorPath}
              errorMessages={errorMessages}
              enableErrorSummary={false}
              onChange={handleChangeText}
              maxLength={questionData.textArea.maxLimit}
              initialTextValue={recurringIssue.whenDidItHappen}
              isLabelTextTitle={false}
            />
          </div>
        )
      }
    />
  );
};
